import TranslationsDropdown from "@src/features/app/dialogs/translationSetDialogs/TranslationsDropdown";
import Text from "@src/lib/ui/Text";
import {
  Popover,
  PopoverTrigger,
  PopoverAnchor,
  PopoverContent,
} from "@src/lib/ui/Popover";
import { TranslationSetFragment } from "@src/fragments.generated";
import { updateTranslationSet } from "@src/features/contentDetail/utils/translationSets";
import { cn } from "@src/lib/utils/cn";
import { AnimatePresence, motion } from "framer-motion";
import LanguageIcon from "@src/lib/icons/18px/language";
import {
  forwardRef,
  HTMLInputTypeAttribute,
  KeyboardEventHandler,
  ReactNode,
  useState,
} from "react";

type Props = {
  value: TranslationSetFragment;
  onChange: (newValue: TranslationSetFragment) => void;
  placeholder?: string;
  onFocus?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  height?: "40px" | "32px";
  width?: React.CSSProperties["width"];
  label?: string;
  subtext?: string;
  leftIcon?: ReactNode;
  containerClassName?: string;
  inputClassName?: string;
  inputType?: HTMLInputTypeAttribute;
  disabled?: boolean;
  error?: boolean;
  hideBorder?: boolean;
  autoFocus?: boolean;
};

const TranslationTextField = forwardRef<HTMLInputElement, Props>(
  (
    {
      value,
      onChange,
      placeholder = "",
      onFocus,
      onMouseEnter,
      onMouseLeave,
      onKeyDown,
      height = "32px",
      width = "100%",
      label,
      subtext,
      leftIcon,
      containerClassName,
      inputClassName,
      inputType = "text",
      disabled = false,
      error = false,
      hideBorder = false,
      autoFocus,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);

    return (
      <div
        className={cn("inline-flex flex-col", containerClassName)}
        style={{ width }}
      >
        {label && <Text variant="compact-title">{label}</Text>}
        <div className="relative w-full">
          <input
            ref={ref}
            type={inputType}
            disabled={disabled}
            className={cn(
              "w-full outline-none rounded-lg text-sm font-normal box-border overflow-x-hidden whitespace-nowrap text-ellipsis font-inter",
              "placeholder:text-gray-5",
              "text-black bg-white",
              height === "32px" ? "px-[27px] pl-2" : "px-[30px] pl-2.5",
              !!leftIcon && (height === "32px" ? "pl-[30px]" : "pl-8"),
              !error &&
                !hideBorder &&
                "border border-gray-5-alpha hover:border-blue-9 focus:border-2 focus:border-blue-3 disabled:border-gray-5-alpha disabled:bg-gray-4-alpha",
              error &&
                "border border-red-9 hover:border-red-9 focus:border-red-9",
              hideBorder &&
                "border border-transparent hover:border-transparent focus:border-transparent disabled:border-transparent",
              inputClassName,
            )}
            placeholder={placeholder}
            style={{
              height,
            }}
            value={value?.en}
            onFocus={onFocus && (() => onFocus())}
            onMouseEnter={onMouseEnter && (() => onMouseEnter())}
            onMouseLeave={onMouseLeave && (() => onMouseLeave())}
            onInput={(e) => {
              onChange(
                updateTranslationSet({
                  ts: value,
                  lang: "en",
                  text: (e.target as HTMLInputElement).value,
                }),
              );
            }}
            onKeyDown={(e) => (onKeyDown ? onKeyDown(e) : {})}
            autoFocus={autoFocus}
          />
          {leftIcon && (
            <span
              className="absolute text-gray-8"
              style={{
                top: "50%",
                transform: "translateY(-50%)",
                left: height === "32px" ? 6 : 8,
              }}
            >
              {leftIcon}
            </span>
          )}
          <Popover
            open={open}
            onOpenChange={(open) => {
              setOpen(open);
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                right: height === "32px" ? 4 : 8,
              }}
              className="flex rounded-md hover:bg-gray-4-alpha"
            >
              <PopoverTrigger asChild>
                <PopoverAnchor>
                  <div className="flex size-[24px] items-center justify-center">
                    <LanguageIcon className="size-4 text-gray-8" />
                  </div>
                </PopoverAnchor>
              </PopoverTrigger>
            </div>
            <AnimatePresence>
              <PopoverContent
                asChild
                className="z-[9999] mt-2 size-full rounded-lg border-none bg-white p-2 pl-4 shadow-[0px_0px_12px_6px_rgba(142,145,146,0.15),0px_0px_4px_5px_rgba(142,145,146,0.15)] outline-none focus:border-none focus:outline-none"
                align="start"
              >
                <motion.div
                  initial={{ scaleX: 1, scaleY: 0.6, opacity: 0 }}
                  animate={{
                    scaleY: 1,
                    opacity: 1,
                    transition: { type: "spring", duration: 0.2 },
                  }}
                  style={{ originY: 0, originX: 1 }}
                >
                  <TranslationsDropdown value={value} onChange={onChange} />
                </motion.div>
              </PopoverContent>
            </AnimatePresence>
          </Popover>
        </div>
        {subtext && (
          <Text
            variant="fine-print-title"
            className={error ? "text-red-9" : "text-gray-7"}
            style={{
              margin: height === "32px" ? "4px 0 0 10px" : "4px 0 0 12px",
            }}
          >
            {subtext}
          </Text>
        )}
      </div>
    );
  },
);

TranslationTextField.displayName = "TranslationTextField";

export default TranslationTextField;
